import { tagsApi } from '../api/tags.js';
import { aiApi } from '../api/ai.js';

// Inline HTML — embed inside a parent template that has x-data="tagInput(...)"
export const TAG_INPUT_HTML = `
<div class="relative" @click.outside="open = false">
  <div class="min-h-[40px] w-full border border-gray-300 rounded-lg px-2 py-1.5
              flex flex-wrap gap-1.5 cursor-text bg-white
              focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500"
       @click="$el.querySelector('input')?.focus()">
    <template x-for="(tag, i) in selected" :key="tag">
      <span class="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs
                   font-medium px-2.5 py-0.5 rounded-full select-none">
        <span x-text="tag"></span>
        <button type="button" @click.stop="removeTag(i)"
                class="ml-0.5 text-blue-400 hover:text-blue-700 leading-none font-bold">&times;</button>
      </span>
    </template>
    <input type="text" x-model="inputVal"
           @focus="onFocus()" @input="onInput()" @keydown="onKeydown($event)"
           class="flex-1 min-w-[90px] outline-none text-sm bg-transparent py-0.5"
           :placeholder="selected.length === 0 ? $t('tags.input_placeholder') : ''" />
  </div>
  <!-- AI suggest button -->
  <button type="button" @click="suggestAiTags()"
          :disabled="aiSuggesting"
          class="mt-1.5 text-xs text-purple-600 hover:text-purple-800 flex items-center gap-1 disabled:opacity-50 transition-colors">
    <span x-show="!aiSuggesting">✨ AI suggest</span>
    <span x-show="aiSuggesting" class="flex items-center gap-1"><svg class="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Suggesting…</span>
  </button>
  <!-- AI suggestions chips -->
  <template x-if="aiSuggestions.length > 0">
    <div class="mt-1.5 flex flex-wrap gap-1.5">
      <template x-for="tag in aiSuggestions" :key="tag">
        <button type="button" @click="addAiTag(tag)"
                class="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium px-2 py-0.5 rounded-full hover:bg-purple-100 transition-colors">
          + <span x-text="tag"></span>
        </button>
      </template>
    </div>
  </template>
  <!-- Dropdown -->
  <div x-show="open && (filtered.length > 0 || (inputVal.trim() && !selected.includes(inputVal.trim())))"
       x-transition:enter="transition ease-out duration-100"
       x-transition:enter-start="opacity-0 scale-95"
       x-transition:enter-end="opacity-100 scale-100"
       class="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl
              shadow-xl overflow-hidden" style="max-height:220px;overflow-y:auto">
    <template x-for="tag in filtered" :key="tag">
      <button type="button" @mousedown.prevent="selectSuggestion(tag)"
              class="w-full text-left px-3 py-2.5 text-sm hover:bg-blue-50
                     flex items-center gap-2.5 transition-colors">
        <span class="w-2 h-2 rounded-full bg-blue-400 shrink-0"></span>
        <span x-text="tag" class="font-medium text-gray-800"></span>
      </button>
    </template>
    <template x-if="inputVal.trim() && !selected.includes(inputVal.trim()) && !filtered.includes(inputVal.trim())">
      <button type="button" @mousedown.prevent="addTag(inputVal)"
              class="w-full text-left px-3 py-2.5 text-sm hover:bg-emerald-50 text-emerald-700
                     flex items-center gap-2.5 border-t border-gray-100 transition-colors">
        <span class="text-base leading-none">＋</span>
        <span x-text="$t('tags.create_new') + ': "' + inputVal.trim() + '"'"></span>
      </button>
    </template>
  </div>
</div>
`;

// Inline HTML — embed inside a parent template that has x-data="tagFilter(...)"
export const TAG_FILTER_HTML = `
<div class="flex flex-wrap gap-2 items-center">
  <button @click="setTag('')"
          :class="activeTag === '' ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
          class="px-3 py-1 rounded-full text-xs font-medium transition-colors"
          x-text="$t('tags.filter_all')"></button>
  <template x-for="tag in tags" :key="tag">
    <button @click="setTag(tag)"
            :class="activeTag === tag
              ? 'bg-blue-600 text-white shadow-sm'
              : 'bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200'"
            class="px-3 py-1 rounded-full text-xs font-medium transition-colors"
            x-text="tag"></button>
  </template>
  <span x-show="tags.length === 0 && !loading"
        class="text-xs text-gray-400 italic" x-text="$t('tags.none_yet')"></span>
</div>
`;

export function tagInput(config = {}) {
  return {
    entityType: config.entityType ?? '',
    _getContext: config.getContext ?? null,
    selected: Array.isArray(config.modelValue) ? [...config.modelValue] : [],
    _onChange: config.onChange ?? null,

    inputVal: '',
    allSuggestions: [],
    suggestionsLoaded: false,
    open: false,
    _debounceTimer: null,
    aiSuggesting: false,
    aiSuggestions: [],

    get filtered() {
      const q = this.inputVal.toLowerCase().trim();
      const sel = new Set(this.selected);
      const base = this.allSuggestions.filter(t => !sel.has(t));
      if (!q) return base.slice(0, 10);
      return base.filter(t => t.toLowerCase().includes(q)).slice(0, 10);
    },

    async loadSuggestions() {
      if (this.suggestionsLoaded) return;
      try {
        const { data } = await tagsApi.list(this.entityType);
        this.allSuggestions = data.data ?? [];
        this.suggestionsLoaded = true;
      } catch {}
    },

    onFocus() {
      this.open = true;
      this.loadSuggestions();
    },

    onInput() {
      this.open = true;
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(async () => {
        const q = this.inputVal.trim();
        if (!q) return;
        try {
          const { data } = await tagsApi.list(this.entityType, q);
          this.allSuggestions = Array.from(new Set([...this.allSuggestions, ...(data.data ?? [])]));
        } catch {}
      }, 300);
    },

    onKeydown(e) {
      if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
        const val = this.inputVal.trim().replace(/,$/, '');
        if (val) { e.preventDefault(); this.addTag(val); }
      } else if (e.key === 'Backspace' && this.inputVal === '' && this.selected.length > 0) {
        this.removeTag(this.selected.length - 1);
      } else if (e.key === 'Escape') {
        this.open = false;
      }
    },

    addTag(tag) {
      tag = tag.trim();
      if (!tag || this.selected.includes(tag)) { this.inputVal = ''; return; }
      this.selected = [...this.selected, tag];
      if (!this.allSuggestions.includes(tag)) this.allSuggestions = [...this.allSuggestions, tag];
      this.inputVal = '';
      this.open = false;
      this.notify();
    },

    removeTag(i) {
      this.selected = this.selected.filter((_, idx) => idx !== i);
      this.notify();
    },

    selectSuggestion(tag) { this.addTag(tag); },

    async suggestAiTags() {
      this.aiSuggesting = true;
      this.aiSuggestions = [];
      try {
        const context = this._getContext ? this._getContext() : '';
        const { data } = await aiApi.suggestTags(this.entityType, context, this.selected);
        this.aiSuggestions = (data.data?.tags ?? []).filter(t => !this.selected.includes(t));
      } catch {} finally {
        this.aiSuggesting = false;
      }
    },

    addAiTag(tag) {
      this.addTag(tag);
      this.aiSuggestions = this.aiSuggestions.filter(t => t !== tag);
    },

    notify() {
      if (this._onChange) this._onChange([...this.selected]);
    },
  };
}

export function tagFilterBar(config = {}) {
  return {
    entityType: config.entityType ?? '',
    _onSelect: config.onSelect ?? null,
    activeTag: '',
    tags: [],
    loading: false,

    async loadTags() {
      this.loading = true;
      try {
        const { data } = await tagsApi.list(this.entityType);
        this.tags = data.data ?? [];
      } catch {} finally {
        this.loading = false;
      }
    },

    setTag(tag) {
      this.activeTag = tag;
      if (this._onSelect) this._onSelect(tag);
    },
  };
}

export default tagInput;
